import React, { useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from '../../../app/LanguageContext';
import { PRODUCT_DEFAULT_IMAGE } from '../../../modules/medias/public/MediaGallery';
import CatalogItemEditDialog from '../private/CatalogItemEditDialog';
import {
  getEntryDisplayCode,
  getEntryDisplayName,
  getEntryThumbnailUrl,
  newClientId,
  normalizeCatalogProductItem,
  summarizeProductItemPrice,
} from '../private/catalogCompositionUtils';

const emptyDialogState = () => ({
  open: false,
  sessionId: null,
  mode: 'create',
  entryType: 'PRODUCT',
  itemIndex: null,
});

const ProductGridItemsEditor = ({ items, readOnly, onChange, minItems = 0, maxItems = 0 }) => {
  const { translate } = useTranslation();
  const [addAnchor, setAddAnchor] = useState(null);
  const [dialog, setDialog] = useState(emptyDialogState);
  const dialogSessionRef = useRef(0);
  const min = Math.max(0, Number(minItems) || 0);
  const max = Number(maxItems) > 0 ? Number(maxItems) : 0;

  const normalizedItems = useMemo(
    () => (Array.isArray(items) ? items : []).map((item) => normalizeCatalogProductItem(item)),
    [items],
  );

  const canAdd = !readOnly && (max <= 0 || normalizedItems.length < max);
  const canRemove = () => !readOnly && normalizedItems.length > min;

  const nextDialogSession = () => {
    dialogSessionRef.current += 1;
    return dialogSessionRef.current;
  };

  const emitItems = (nextItems) => {
    let next = nextItems.map((item, index) => normalizeCatalogProductItem({ ...item, display_order: index }));
    if (max > 0 && next.length > max) {
      next = next.slice(0, max);
    }
    onChange?.(next);
  };

  const openCreate = (entryType) => {
    if (!canAdd) return;
    setAddAnchor(null);
    setDialog({
      open: true,
      sessionId: nextDialogSession(),
      mode: 'create',
      entryType,
      itemIndex: null,
    });
  };

  const openEdit = (index) => {
    setDialog({
      open: true,
      sessionId: nextDialogSession(),
      mode: 'edit',
      entryType: (normalizedItems[index]?.item_type || 'PRODUCT').toUpperCase() === 'SERVICE' ? 'SERVICE' : 'PRODUCT',
      itemIndex: index,
    });
  };

  const closeDialog = () => setDialog(emptyDialogState());

  const handleDialogSave = ({ entryType, productItem }) => {
    if (entryType === 'KIT') return;
    const next = [...normalizedItems];
    const saved = normalizeCatalogProductItem({
      ...productItem,
      id: productItem.id || newClientId(),
      item_type: entryType,
      is_available: productItem.is_available !== false,
    });
    if (dialog.mode === 'create') {
      if (!canAdd) return;
      next.push(saved);
    } else if (dialog.itemIndex != null && dialog.itemIndex >= 0) {
      next[dialog.itemIndex] = saved;
    }
    emitItems(next);
    closeDialog();
  };

  const handleDialogDelete = () => {
    if (dialog.itemIndex == null) return;
    emitItems(normalizedItems.filter((_, index) => index !== dialog.itemIndex));
    closeDialog();
  };

  const moveItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= normalizedItems.length) return;
    const next = [...normalizedItems];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    emitItems(next);
  };

  const toggleEnabled = (index, enabled) => {
    emitItems(
      normalizedItems.map((item, idx) => (idx === index ? { ...item, is_available: enabled } : item)),
    );
  };

  const removeItem = (index) => {
    if (!canRemove()) return;
    emitItems(normalizedItems.filter((_, idx) => idx !== index));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {translate('catalog.blocks.sectionItems')}
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={!canAdd}
            onClick={(event) => setAddAnchor(event.currentTarget)}
          >
            {translate('catalog.composition_add')}
          </Button>
        )}
      </Box>
      {(min > 0 || max > 0) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {max > 0
            ? translate('catalog.templates.list_items_limits', { min, max })
            : translate('catalog.templates.list_items_limits_min_only', { min })}
        </Typography>
      )}
      <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
        <MenuItem onClick={() => openCreate('PRODUCT')}>{translate('catalog.add_product_only')}</MenuItem>
        <MenuItem onClick={() => openCreate('SERVICE')}>{translate('catalog.add_service')}</MenuItem>
      </Menu>
      {normalizedItems.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {translate('catalog.blocks.sectionItemsEmpty')}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {normalizedItems.map((item, index) => {
            const thumb = getEntryThumbnailUrl({ entryType: 'PRODUCT', productItem: item }) || PRODUCT_DEFAULT_IMAGE;
            const code = getEntryDisplayCode({ entryType: 'PRODUCT', productItem: item });
            const name = getEntryDisplayName({ entryType: 'PRODUCT', productItem: item }) || '—';
            const priceSummary = summarizeProductItemPrice(item);
            return (
              <ListItem
                key={item.id || index}
                divider={index < normalizedItems.length - 1}
                secondaryAction={
                  !readOnly && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title={translate('catalog.item_enabled')}>
                        <Switch
                          size="small"
                          checked={item.is_available !== false}
                          onChange={(event) => toggleEnabled(index, event.target.checked)}
                        />
                      </Tooltip>
                      <IconButton size="small" onClick={() => openEdit(index)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeItem(index)}
                        disabled={!canRemove()}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                  <IconButton size="small" disabled={readOnly || index === 0} onClick={() => moveItem(index, -1)}>
                    <KeyboardArrowUpIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={readOnly || index === normalizedItems.length - 1}
                    onClick={() => moveItem(index, 1)}
                  >
                    <KeyboardArrowDownIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <ListItemAvatar>
                  <Avatar variant="rounded" src={thumb} alt={name} sx={{ width: 44, height: 44 }} />
                </ListItemAvatar>
                <ListItemText
                  primary={name}
                  secondary={[code, priceSummary].filter(Boolean).join(' · ')}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
      <CatalogItemEditDialog
        open={dialog.open}
        sessionId={dialog.sessionId}
        mode={dialog.mode}
        entryType={dialog.entryType}
        initialProductItem={
          dialog.itemIndex != null ? normalizedItems[dialog.itemIndex] : null
        }
        initialKit={null}
        productItems={normalizedItems}
        displayOrder={dialog.itemIndex ?? normalizedItems.length}
        disabled={readOnly}
        onClose={closeDialog}
        onSave={handleDialogSave}
        onDelete={dialog.mode === 'edit' ? handleDialogDelete : undefined}
      />
    </Box>
  );
};

export default ProductGridItemsEditor;
